import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatRM, formatDateTime, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Image as ImageIcon } from "lucide-react";

export default function PublicApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [comments, setComments] = useState("");
  const [signature, setSignature] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/approval/${token}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [token]);

  const respond = async (approved) => {
    if (approved && !signature.trim()) return toast.error("Please type your name as signature");
    if (!approved && !comments.trim()) return toast.error("Please describe the changes required");
    try {
      await api.post(`/public/approval/${token}/respond`, { approved, comments, signature });
      setDone(approved ? "approved" : "revision");
    } catch { toast.error("Failed"); }
  };

  if (data === false) return <div className="min-h-screen flex items-center justify-center"><Card className="p-8">Invalid link</Card></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  const { job, artworks } = data;
  const latest = artworks[0];
  const paid = (job.deposit_paid || 0) + (job.balance_paid || 0);

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          {done === "approved" ? <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" /> : <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />}
          <h2 className="text-2xl font-display font-bold mb-2">{done === "approved" ? "Approved!" : "Revision Requested"}</h2>
          <p className="text-muted-foreground">{done === "approved" ? "Thank you. Production will begin now." : "Thank you. Our designer will make the changes."}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <img src="/rozfa-logo.jpg" alt="Rozfa Print Ops" className="h-12 w-auto dark:hidden" />
          <img src="/rozfa-logo-white.png" alt="Rozfa Print Ops" className="h-12 w-auto hidden dark:block" />
          <div>
            <div className="text-xs text-muted-foreground font-mono">Job Approval Portal</div>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <div className="font-mono text-lg font-bold">{job.job_number}</div>
              <div className="text-sm text-muted-foreground">{job.customer_name}</div>
            </div>
            <Badge>{job.status}</Badge>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Product:</span> {job.product}</div>
            <div><span className="text-muted-foreground">Quantity:</span> {job.quantity}</div>
            {job.dimensions && <div><span className="text-muted-foreground">Size:</span> {job.dimensions}</div>}
            {job.material && <div><span className="text-muted-foreground">Material:</span> {job.material}</div>}
            <div><span className="text-muted-foreground">Total:</span> <span className="font-bold">{formatRM(job.total_price)}</span></div>
            <div><span className="text-muted-foreground">Paid:</span> {formatRM(paid)}</div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-3">Artwork Preview</h3>
          {latest ? (
            <div>
              <div className="border rounded-lg overflow-hidden bg-muted/30">
                {/(jpg|jpeg|png|gif|webp)$/i.test(latest.filename) ? (
                  <img
                    src={`${BACKEND_URL}/api/public/files/${encodeURIComponent(latest.file_path.split("/").pop())}`}
                    alt="artwork"
                    className="w-full max-h-[500px] object-contain"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    <ImageIcon className="w-12 h-12" />
                  </div>
                )}
              </div>
              <div className="mt-2 text-xs text-muted-foreground font-mono">Version {latest.version} • {latest.filename}</div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-8">No artwork uploaded yet. Please check back later.</div>
          )}
        </Card>

        {latest && latest.status === "pending" && (
          <Card className="p-6 space-y-4">
            <h3 className="font-display font-bold">Approval Response</h3>
            <div>
              <Label>Digital Signature (type your full name)</Label>
              <Input value={signature} onChange={(e)=>setSignature(e.target.value)} placeholder="Full Name" data-testid="approval-signature" />
            </div>
            <div>
              <Label>Comments / Revision Notes</Label>
              <Textarea rows={3} value={comments} onChange={(e)=>setComments(e.target.value)} placeholder="If you need changes, write them here..." data-testid="approval-comments" />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => respond(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve Design
              </Button>
              <Button onClick={() => respond(false)} variant="outline" className="flex-1 border-amber-500 text-amber-700" data-testid="revision-btn">
                <XCircle className="w-4 h-4 mr-1" /> Request Revision
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
